import React, { useState, useEffect } from "react";
import "./DynamicPreviousCountriesTable.css";
import DateInput from "./DateInput";

interface PreviousCountryEntry {
  country: string;
  status: string;
  other: string;
  fromDate: string;
  toDate: string;
}

interface DynamicPreviousCountriesTableProps {
  value: { [key: string]: PreviousCountryEntry };
  onChange: (entries: { [key: string]: PreviousCountryEntry }) => void;
  required?: boolean;
}

const DynamicPreviousCountriesTable: React.FC<
  DynamicPreviousCountriesTableProps
> = ({ value, onChange, required = false }) => {
  const [entries, setEntries] = useState<{
    [key: string]: PreviousCountryEntry;
  }>(value || {});

  useEffect(() => {
    onChange(entries);
  }, [entries, onChange]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === "Enter") {
        event.preventDefault();
        addEntry();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const addEntry = () => {
    const entryCount = Object.keys(entries).length;
    const newKey = `previousCountry${entryCount + 1}`;

    const newEntry: PreviousCountryEntry = {
      country: "",
      status: "",
      other: "",
      fromDate: "",
      toDate: "",
    };

    const updatedEntries = { ...entries, [newKey]: newEntry };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const removeEntry = (key: string) => {
    const { [key]: removed, ...remainingEntries } = entries;

    // Renumber the remaining entries
    const renumberedEntries: { [key: string]: PreviousCountryEntry } = {};
    const remainingKeys = Object.keys(remainingEntries).sort();

    remainingKeys.forEach((oldKey, index) => {
      const newKey = `previousCountry${index + 1}`;
      renumberedEntries[newKey] = remainingEntries[oldKey];
    });

    setEntries(renumberedEntries);
    onChange(renumberedEntries);
  };

  const updateEntry = (
    key: string,
    field: keyof PreviousCountryEntry,
    value: string
  ) => {
    const updatedEntries = {
      ...entries,
      [key]: { ...entries[key], [field]: value },
    };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  // Initialize with at least one entry if empty
  useEffect(() => {
    if (Object.keys(entries).length === 0) {
      addEntry();
    }
  }, []);

  const entryKeys = Object.keys(entries).sort();

  return (
    <div className="dynamic-previous-countries-table">
      <div className="table-header">
        <h4>Previous Countries of Residence</h4>
        <button type="button" className="add-btn" onClick={addEntry}>
          + Add Previous Country Entry
        </button>
      </div>

      {entryKeys.length === 0 ? (
        <div className="empty-state">
          <p>No previous countries entered yet.</p>
          <button type="button" className="add-btn" onClick={addEntry}>
            + Add Previous Country Entry
          </button>
        </div>
      ) : (
        <div className="entries-container">
          {entryKeys.map((key, index) => (
            <div key={key} className="entry-card">
              <div className="entry-header">
                <div className="entry-number">
                  <span className="entry-badge">{index + 1}</span>
                  <h5>Entry {index + 1}</h5>
                </div>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeEntry(key)}
                  title="Remove this previous country entry"
                >
                  ×
                </button>
              </div>

              <div className="entry-form">
                <div className="form-row form-row-1">
                  <div className="field-group">
                    <label>Country</label>
                    <input
                      type="text"
                      value={entries[key].country}
                      onChange={(e) =>
                        updateEntry(key, "country", e.target.value)
                      }
                      placeholder="e.g., United States"
                    />
                  </div>

                  <div className="field-group">
                    <label>Status</label>
                    <input
                      type="text"
                      value={entries[key].status}
                      onChange={(e) =>
                        updateEntry(key, "status", e.target.value)
                      }
                      placeholder="e.g., Student, Worker"
                    />
                  </div>

                  <div className="field-group">
                    <label>Other</label>
                    <input
                      type="text"
                      value={entries[key].other}
                      onChange={(e) =>
                        updateEntry(key, "other", e.target.value)
                      }
                      placeholder="Additional details"
                    />
                  </div>
                </div>

                <div className="form-row form-row-2">
                  <div className="field-group">
                    <label>From</label>
                    <input
                      type="date"
                      value={entries[key].fromDate}
                      onChange={(e) =>
                        updateEntry(key, "fromDate", e.target.value)
                      }
                    />
                  </div>

                  <div className="field-group">
                    <label>To</label>
                    <input
                      type="date"
                      value={entries[key].toDate}
                      onChange={(e) =>
                        updateEntry(key, "toDate", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {entryKeys.length > 0 && (
        <div className="table-footer">
          <button
            type="button"
            className="add-btn secondary"
            onClick={addEntry}
            title="Add another previous country entry (Ctrl+Enter)"
          >
            + Add Another Entry
          </button>
        </div>
      )}

      {required && entryKeys.length === 0 && (
        <small className="error-text">
          At least one previous country entry is required
        </small>
      )}
    </div>
  );
};

export default DynamicPreviousCountriesTable;
